import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, CheckCircle, Lock, LogIn, Mail, Shield } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { VisualFeedbackWidget } from '@/components/feedback/VisualFeedbackWidget';
import { usePageChromeSetter } from '@/components/layout/pageChrome';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';
import { OnboardingWizard } from '@/components/onboarding/OnboardingWizard';
import { PixelFieldScreen } from '@/components/onboarding/PixelFieldScreen';
import { RosePreview } from '@/components/onboarding/RosePreview';
import { getWelcome } from '@/components/onboarding/welcomeFlow';
import { TurnstileCaptcha } from '@/components/TurnstileCaptcha';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { BackofficeAuthProvider, useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import { isSupabaseConfigured } from '@/lib/supabase';
import { fetchOnboardingStatus } from '@/services/onboarding';

interface AuthGuardProps {
    children: ReactNode;
    fallback?: ReactNode;
}

function SupabaseNotConfiguredScreen() {
    return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50">
            <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-xl shadow-2xl border border-orange-100">
                <div className="text-center">
                    <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Shield className="h-10 w-10 text-orange-600" />
                    </div>

                    <h2 className="text-3xl font-bold text-gray-900 mb-3">
                        Authentication Required
                    </h2>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                        <p className="text-orange-800 font-medium mb-2">
                            Supabase is not configured
                        </p>
                        <p className="text-orange-700 text-sm">
                            Please set{' '}
                            <code className="bg-orange-100 px-1 rounded">
                                VITE_DIRECT_SUPABASE_URL
                            </code>{' '}
                            and{' '}
                            <code className="bg-orange-100 px-1 rounded">
                                VITE_DIRECT_SUPABASE_ANON_KEY
                            </code>{' '}
                            environment variables.
                        </p>
                    </div>

                    <p className="text-gray-500 text-sm">
                        Contact your administrator if you need help with configuration.
                    </p>
                </div>
            </div>
        </div>
    );
}

function LoadingScreen() {
    return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading...</p>
            </div>
        </div>
    );
}

/**
 * Shown to an authenticated user who has no backoffice provisioning in
 * production. Self-serve signup is not offered there, so this is a teammate of
 * an existing (or not-yet-created) Rose account who must be invited — not a new
 * account to create. Point them at an admin instead of the onboarding wizard,
 * which would otherwise try to recreate an already-registered account.
 */
function NoAccessScreen({ email, onSignOut }: { email: string; onSignOut: () => void }) {
    // Rendered outside AppLayout, so nothing derives its tab title from a route —
    // register it on the same channel <Page> uses (DocumentTitle stays the single
    // writer). Otherwise the tab keeps the underlying route's title (IX-4913).
    const setChrome = usePageChromeSetter();
    useLayoutEffect(() => {
        setChrome({ title: 'Access' });
        return () => setChrome(null);
    }, [setChrome]);

    return (
        <PixelFieldScreen icon={<Lock className="size-7 text-accent-foreground" />}>
            <h1 className="mt-6 text-balance text-2xl font-bold leading-tight tracking-tight text-primary-foreground">
                You don’t have access yet
            </h1>
            <p className="mt-2 text-pretty text-sm leading-relaxed text-primary-foreground/80">
                {email ? `You’re signed in as ${email}, which ` : 'This account '}
                isn’t linked to a Rose account. If your company already uses Rose, ask a teammate
                who administers it to invite you. Otherwise, contact support@userose.ai.
            </p>
            <Button
                type="button"
                variant="outline"
                className="mt-7 border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                onClick={onSignOut}
            >
                Sign out
            </Button>
        </PixelFieldScreen>
    );
}

/**
 * Shown when the backoffice profile fetch itself failed (network, JWT race, 5xx)
 * rather than returning "no row". We can't tell if this user is provisioned, so
 * we must NOT fall through to the onboarding wizard (which would try to recreate
 * an existing account). Offer a retry; the query also refetches on focus/reconnect.
 */
function ProfileErrorScreen({
    onRetry,
    onSignOut,
}: {
    onRetry: () => void;
    onSignOut: () => void;
}) {
    const setChrome = usePageChromeSetter();
    useLayoutEffect(() => {
        setChrome({ title: 'Something went wrong' });
        return () => setChrome(null);
    }, [setChrome]);

    return (
        <PixelFieldScreen icon={<Lock className="size-7 text-accent-foreground" />}>
            <h1 className="mt-6 text-balance text-2xl font-bold leading-tight tracking-tight text-primary-foreground">
                We couldn’t load your account
            </h1>
            <p className="mt-2 text-pretty text-sm leading-relaxed text-primary-foreground/80">
                Something went wrong reaching Rose. Your account is safe — this is a temporary
                error. Try again in a moment.
            </p>
            <div className="mt-7 flex gap-3">
                <Button type="button" onClick={onRetry}>
                    Try again
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    className="border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                    onClick={onSignOut}
                >
                    Sign out
                </Button>
            </div>
        </PixelFieldScreen>
    );
}

/** Staff domain — these testers can create an account with a password, skipping
 *  the email OTP step (mirrors the backend claim gate in onboarding.py). */
const STAFF_EMAIL_DOMAIN = 'userose.ai';

/** Supabase error codes that say nothing about whether the address is registered:
 *  the send never reached the mail stage. Surfacing them cannot enumerate accounts,
 *  and hiding them shows "check your inbox" for a code that was never sent — which
 *  is how a project-wide CAPTCHA misconfiguration went unnoticed (IX-4608). */
const NON_ENUMERATING_OTP_ERRORS = new Set([
    'captcha_failed',
    'over_email_send_rate_limit',
    'over_request_rate_limit',
    'unexpected_failure',
]);

/** Set when "Sign up with Google" is clicked from /signup, so the OAuth callback
 *  can route a staff signer into the onboarding funnel. Uses localStorage so it
 *  survives the cross-origin OAuth redirect. Read + cleared in AuthCallbackPage. */
export const SIGNUP_INTENT_KEY = 'rose_signup_intent';

/** Where AuthCallbackPage sends the browser after a Google sign-in started on the assistant
 *  consent page (IX-5466); without it the round trip lands on `/` and the request is lost. */
export const POST_LOGIN_PATH_KEY = 'rose_post_login_path';

interface LoginScreenProps {
    onSignInWithGoogle: () => Promise<unknown>;
    onSendEmailOtp: (email: string, captchaToken?: string) => Promise<unknown>;
    onVerifyEmailOtp: (email: string, token: string) => Promise<unknown>;
    onSignInWithPassword: (email: string, password: string) => Promise<unknown>;
    onSignUpWithPassword: (
        email: string,
        password: string,
        captchaToken?: string
    ) => Promise<unknown>;
}

/** Google "G" — 4-colour glyph, matching the design's OAuth button. */
function GoogleGlyph() {
    return (
        <svg viewBox="0 0 48 48" className="size-4" aria-hidden="true">
            <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            />
            <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            />
            <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
        </svg>
    );
}

export function LoginScreen({
    onSignInWithGoogle,
    onSendEmailOtp,
    onVerifyEmailOtp,
    onSignInWithPassword,
    onSignUpWithPassword,
}: LoginScreenProps) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [emailOtpSent, setEmailOtpSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [resendStatus, setResendStatus] = useState<'sent' | 'failed' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [captchaRefreshKey, setCaptchaRefreshKey] = useState(0);
    // The code screen renders no CAPTCHA until a resend is asked for: verifying a
    // code needs no token, and Turnstile tokens are single-use, so an eager widget
    // made everyone solve a second challenge to arm a button most never press.
    const [wantsResend, setWantsResend] = useState(false);

    const isLocalDev = import.meta.env.DEV;
    // Marketing site "Get started" links land on /signup — signup-oriented
    // copy, email OTP + Google only (no password signup). The email OTP
    // creates the account (shouldCreateUser) — self-serve onboarding,
    // IX-3768.
    const isSignup = useLocation().pathname.startsWith('/signup');
    // Self-serve signup is not offered in production yet — hide the "Create an
    // account" CTA there. Non-prod (local/test/staging) still shows it.
    const isProd = import.meta.env.VITE_ENVIRONMENT === 'production';
    // Staff (@userose.ai) creating a normal account can bypass the email OTP
    // step — set a password and land straight in the onboarding funnel.
    const isStaffBypass = isSignup && email.trim().toLowerCase().endsWith(`@${STAFF_EMAIL_DOMAIN}`);
    // Supabase CAPTCHA applies to email sign-in as well as signup. Local
    // password login is a separate dev-only path and does not call Supabase's
    // OTP endpoint, so it does not need a CAPTCHA token.
    const requiresCaptcha = !(isLocalDev && !isSignup);

    const resetCaptcha = () => {
        setCaptchaToken(null);
        setCaptchaRefreshKey((key) => key + 1);
    };

    const handleStaffSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim() || !password || !captchaToken) return;
        setIsLoading(true);
        setError(null);
        try {
            await onSignUpWithPassword(email.trim(), password, captchaToken);
            // Success: onAuthStateChange picks up the new session and renders the
            // onboarding funnel. If confirmations are on, no session yet — tell them.
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not create the account');
        } finally {
            resetCaptcha();
            setIsLoading(false);
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim() || !password) return;

        setIsLoading(true);
        setError(null);

        try {
            await onSignInWithPassword(email.trim(), password);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Invalid email or password');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailOtpRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim() || (requiresCaptcha && !captchaToken)) return;

        setIsLoading(true);
        setError(null);
        let sendBlocked = false;

        try {
            if (requiresCaptcha) {
                await onSendEmailOtp(email.trim(), captchaToken ?? undefined);
            } else {
                await onSendEmailOtp(email.trim());
            }
        } catch (err) {
            // Only infrastructure failures are surfaced. Everything else is
            // suppressed so "check your inbox" cannot enumerate accounts.
            const code = (err as { code?: string } | null)?.code;
            if (code && NON_ENUMERATING_OTP_ERRORS.has(code)) {
                sendBlocked = true;
                setError(
                    "We couldn't send your code — this is on our side, not your inbox. Try again in a moment, and contact support@userose.ai if it keeps failing."
                );
            }
        } finally {
            if (requiresCaptcha) resetCaptcha();
            setIsLoading(false);
            setEmailOtpSent(!sendBlocked);
        }
    };

    const handleEmailOtpVerification = async (e: React.FormEvent) => {
        e.preventDefault();
        if (otp.length !== 6) return;

        setIsLoading(true);
        setError(null);

        try {
            await onVerifyEmailOtp(email.trim(), otp);
        } catch {
            setError('That code is invalid or has expired. Request a new code and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async (freshToken?: string) => {
        const token = freshToken ?? captchaToken ?? undefined;
        if (requiresCaptcha && !token) return;
        setIsResending(true);
        setError(null);
        setResendStatus(null);

        try {
            await onSendEmailOtp(email.trim(), requiresCaptcha ? token : undefined);
            setOtp('');
            setResendStatus('sent');
        } catch {
            setResendStatus('failed');
        } finally {
            if (requiresCaptcha) {
                resetCaptcha();
                setWantsResend(false);
            }
            setIsResending(false);
        }
    };

    // Kept in a ref so the widget's onTokenChange identity stays stable — the
    // Turnstile effect keys on that callback and would remount in a loop otherwise.
    const resendRef = useRef(handleResend);
    resendRef.current = handleResend;

    const handleResendToken = useCallback((token: string | null) => {
        setCaptchaToken(token);
        // Managed Turnstile usually resolves without interaction, so solving the
        // challenge is the send: one click on "Send new code", no second step.
        if (token) void resendRef.current(token);
    }, []);

    const handleReset = () => {
        setEmailOtpSent(false);
        setEmail('');
        setPassword('');
        setOtp('');
        setResendStatus(null);
        setError(null);
        setWantsResend(false);
    };

    if (emailOtpSent) {
        return (
            <OnboardingShell title="Enter your code" aside={<RosePreview variant="answer" />}>
                <div className="flex size-12 items-center justify-center rounded-full bg-success-subtle text-success">
                    <CheckCircle className="size-6" />
                </div>
                <h1 className="mt-6 text-3xl font-bold leading-tight tracking-tight">
                    Enter your code
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {isSignup ? (
                        <>
                            We sent a six-digit signup code to{' '}
                            <strong className="text-foreground">{email}</strong>.
                        </>
                    ) : (
                        <>
                            If <strong className="text-foreground">{email}</strong> is registered,
                            you&apos;ll receive a six-digit login code shortly.
                        </>
                    )}{' '}
                    The code expires shortly and can only be used once.
                </p>
                <form onSubmit={handleEmailOtpVerification} className="mt-6 space-y-3">
                    <div>
                        <label
                            htmlFor="email-otp"
                            className="mb-1.5 block text-[13px] font-semibold text-foreground"
                        >
                            Six-digit code
                        </label>
                        <Input
                            id="email-otp"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            pattern="[0-9]*"
                            maxLength={6}
                            autoFocus
                            placeholder="000000"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            aria-invalid={Boolean(error)}
                            className="h-12 text-center font-mono text-xl tracking-[0.35em]"
                        />
                    </div>
                    {error && (
                        <p role="alert" className="text-sm text-destructive">
                            {error}
                        </p>
                    )}
                    <Button
                        type="submit"
                        size="lg"
                        className="w-full"
                        disabled={isLoading || otp.length !== 6}
                    >
                        {isLoading ? 'Verifying…' : 'Verify code'}
                        <ArrowRight className="size-4" />
                    </Button>
                </form>
                {requiresCaptcha && wantsResend && (
                    <div className="mt-4">
                        <TurnstileCaptcha
                            onTokenChange={handleResendToken}
                            refreshKey={captchaRefreshKey}
                        />
                    </div>
                )}
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <Button
                        variant="outline"
                        size="lg"
                        onClick={handleReset}
                        disabled={isLoading || isResending}
                    >
                        <ArrowLeft className="size-4" />
                        Different email
                    </Button>
                    <Button
                        variant="outline"
                        size="lg"
                        onClick={() => {
                            if (!requiresCaptcha) {
                                void handleResend();
                            } else if (wantsResend) {
                                // Re-arm after a challenge that errored or expired.
                                resetCaptcha();
                            } else {
                                setWantsResend(true);
                            }
                        }}
                        disabled={isLoading || isResending}
                    >
                        {isResending ? 'Sending…' : 'Send new code'}
                    </Button>
                </div>
                {resendStatus === 'sent' && (
                    <output className="mt-3 block text-sm text-muted-foreground">
                        If this email can sign in, a new code will arrive shortly.
                    </output>
                )}
                {resendStatus === 'failed' && (
                    <p role="alert" className="mt-3 text-sm text-destructive">
                        We couldn&apos;t request a new code yet. Wait a moment and try again.
                    </p>
                )}
            </OnboardingShell>
        );
    }

    return (
        <OnboardingShell
            title={isSignup ? 'Create your agent' : 'Log in'}
            aside={<RosePreview variant="answer" />}
        >
            <div className="font-mono text-xs uppercase tracking-wide text-accent-foreground">
                {isSignup ? 'Try it free' : 'Welcome back'}
            </div>
            <h1 className="mt-3 text-3xl font-bold leading-tight tracking-tight">
                {isSignup ? 'Create your agent' : 'Log in to Rose'}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {isSignup
                    ? 'One click with Google, or your work email. No password to remember.'
                    : "We'll email you a secure six-digit sign-in code."}
            </p>

            <Button
                onClick={() => {
                    // Mark signup intent so the OAuth callback can route staff into
                    // onboarding even if they already have an account. An OAuth
                    // round trip can be cancelled before the callback clears this
                    // persistent marker, so an explicit login must clear stale intent.
                    if (isSignup) {
                        localStorage.setItem(SIGNUP_INTENT_KEY, '1');
                    } else {
                        localStorage.removeItem(SIGNUP_INTENT_KEY);
                    }
                    void onSignInWithGoogle();
                }}
                variant="outline"
                size="lg"
                className="mt-6 w-full"
            >
                <GoogleGlyph />
                {isSignup ? 'Sign up with Google' : 'Sign in with Google'}
            </Button>

            <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center">
                    <span className="bg-card px-2 font-mono text-xs tracking-wide text-muted-foreground">
                        OR
                    </span>
                </div>
            </div>

            {isLocalDev && !isSignup ? (
                <form onSubmit={handlePasswordSubmit} className="mt-6 space-y-3">
                    <div>
                        <label
                            htmlFor="login-email"
                            className="mb-1.5 block text-[13px] font-semibold text-foreground"
                        >
                            Work email
                        </label>
                        <div className="relative">
                            <Mail className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                id="login-email"
                                type="email"
                                placeholder="you@yourcompany.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="h-12 pl-10 text-base"
                            />
                        </div>
                    </div>
                    <Input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="h-12 text-base"
                    />
                    {error && <p className="text-sm text-destructive">{error}</p>}
                    <Button
                        type="submit"
                        size="lg"
                        className="w-full"
                        disabled={isLoading || !email.trim() || !password}
                    >
                        <LogIn className="size-4" />
                        {isLoading ? 'Signing in…' : 'Sign in'}
                    </Button>
                </form>
            ) : (
                <form
                    onSubmit={isStaffBypass ? handleStaffSignup : handleEmailOtpRequest}
                    className="mt-6 space-y-3"
                >
                    <div>
                        <label
                            htmlFor="login-email"
                            className="mb-1.5 block text-[13px] font-semibold text-foreground"
                        >
                            Work email
                        </label>
                        <div className="relative">
                            <Mail className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                id="login-email"
                                type="email"
                                placeholder="you@yourcompany.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="h-12 pl-10 text-base"
                            />
                        </div>
                    </div>
                    {isStaffBypass && (
                        <>
                            <Input
                                type="password"
                                placeholder="Choose a password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="h-12 text-base"
                            />
                            <p className="text-xs text-muted-foreground">
                                Staff email — skip the email link and pick a password.
                            </p>
                        </>
                    )}
                    {requiresCaptcha && (
                        <TurnstileCaptcha
                            onTokenChange={setCaptchaToken}
                            refreshKey={captchaRefreshKey}
                        />
                    )}
                    {error && <p className="text-sm text-destructive">{error}</p>}
                    <Button
                        type="submit"
                        size="lg"
                        className="w-full"
                        disabled={
                            isLoading ||
                            !email.trim() ||
                            (isStaffBypass && !password) ||
                            (requiresCaptcha && !captchaToken)
                        }
                    >
                        {isLoading
                            ? isStaffBypass
                                ? 'Creating…'
                                : 'Sending…'
                            : isStaffBypass
                              ? 'Create account'
                              : 'Continue with email'}
                        <ArrowRight className="size-4" />
                    </Button>
                </form>
            )}

            {(isSignup || !isProd) && (
                <p className="mt-6 text-sm text-muted-foreground">
                    {isSignup ? (
                        <>
                            Already using Rose?{' '}
                            <Link
                                to="/"
                                className="font-semibold text-accent-foreground hover:underline"
                            >
                                Log in
                            </Link>
                        </>
                    ) : (
                        <>
                            New to Rose?{' '}
                            <Link
                                to="/signup"
                                className="font-semibold text-accent-foreground hover:underline"
                            >
                                Create an account
                            </Link>
                        </>
                    )}
                </p>
            )}
        </OnboardingShell>
    );
}

/**
 * Inner component that uses BackofficeAuthContext
 */
function AuthGuardInner({ children, fallback }: AuthGuardProps) {
    const {
        user,
        loading: authLoading,
        signInWithGoogle,
        sendEmailOtp,
        verifyEmailOtp,
        signInWithPassword,
        signUpWithPassword,
        signOut,
    } = useAuth();
    const {
        backofficeUser,
        isStaff,
        loading: boLoading,
        hasUnresolvedProfileError,
        refetch: refetchBackofficeUser,
    } = useBackofficeAuth();
    const location = useLocation();
    const onSignupPath = location.pathname.startsWith('/signup');
    const onWelcomePath = location.pathname.startsWith('/welcome/');

    // Only show full-screen spinner during initial bootstrap.
    // After first load, background refetches keep stale data visible.
    const isInitialLoad = authLoading || (boLoading && !backofficeUser);

    // For a signed-in user with no backoffice row, ask the admin API whether
    // they may still self-serve onboard. That decides the next screen: eligible
    // → onboarding wizard (first from their company); not eligible → access
    // screen (a colleague to invite, or a personal mailbox the claim rejects).
    // Gated so it only fires once the two auth loads resolve to that state.
    const onboardingStatus = useQuery({
        queryKey: ['onboarding', 'can-onboard', user?.id ?? null],
        queryFn: fetchOnboardingStatus,
        enabled: Boolean(user) && !backofficeUser && !boLoading,
        staleTime: 5 * 60 * 1000,
        retry: 1,
    });

    if (isInitialLoad) {
        return <LoadingScreen />;
    }

    if (!user) {
        if (fallback) {
            return <>{fallback}</>;
        }
        return (
            <LoginScreen
                onSignInWithGoogle={signInWithGoogle}
                onSendEmailOtp={sendEmailOtp}
                onVerifyEmailOtp={verifyEmailOtp}
                onSignInWithPassword={signInWithPassword}
                onSignUpWithPassword={signUpWithPassword}
            />
        );
    }

    // The backoffice profile fetch failed with no prior successful result, so we
    // can't tell whether this user is provisioned. Never fall through to the
    // wizard — that would try to re-onboard an existing account. This gates on
    // `hasUnresolvedProfileError` (data never resolved), NOT a background-refetch
    // failure over cached data: a real prospect who already loaded `null` keeps
    // their wizard/welcome flow instead of being bounced to this screen.
    if (!backofficeUser && hasUnresolvedProfileError) {
        return (
            <ProfileErrorScreen onRetry={() => void refetchBackofficeUser()} onSignOut={signOut} />
        );
    }

    // Authenticated but not provisioned yet — self-serve onboarding (IX-3768).
    if (!backofficeUser) {
        if (onWelcomePath && getWelcome()?.claimPending) {
            return (
                <>
                    {children}
                    <VisualFeedbackWidget floating />
                </>
            );
        }
        // Wait for the eligibility check before choosing a screen, so we never
        // flash the wizard and then swap it for the access screen.
        if (onboardingStatus.isLoading) {
            return <LoadingScreen />;
        }
        // Fail open to the wizard: on a status error (data undefined) a genuine
        // first-time signup is never blocked; the claim step still rejects a
        // duplicate or a personal mailbox. Route ineligible users — a colleague
        // of an existing company, or a personal mailbox the claim would reject —
        // to the access screen instead of a wizard they cannot finish.
        const canOnboard = onboardingStatus.data?.canOnboard ?? true;
        if (!canOnboard) {
            return <NoAccessScreen email={user.email || ''} onSignOut={signOut} />;
        }
        return (
            <>
                <OnboardingWizard email={user.email || ''} onSignOut={signOut} />
                <VisualFeedbackWidget floating />
            </>
        );
    }

    // A superadmin (backoffice is_admin) can re-enter the onboarding funnel to
    // test it even when already provisioned — by visiting /signup. The wizard's
    // "Force re-onboard" toggle handles the test client. Non-staff hit the redirect.
    if (isStaff && onSignupPath) {
        return (
            <>
                <OnboardingWizard email={user.email || ''} onSignOut={signOut} isStaff />
                <VisualFeedbackWidget floating />
            </>
        );
    }

    return (
        <>
            {children}
            {/* The welcome flow renders outside AppLayout, so it has no header
                for the trigger to sit in. */}
            {onWelcomePath && <VisualFeedbackWidget floating />}
        </>
    );
}

/**
 * AuthGuard wraps children with BackofficeAuthProvider and checks access
 */
export function AuthGuard({ children, fallback }: AuthGuardProps) {
    // Check if Supabase is configured - block access if not
    if (!isSupabaseConfigured()) {
        return <SupabaseNotConfiguredScreen />;
    }

    return (
        <BackofficeAuthProvider>
            <AuthGuardInner fallback={fallback}>{children}</AuthGuardInner>
        </BackofficeAuthProvider>
    );
}
